
import { getCommonPrintStyles, getHeaderHtml, getFooterHtml, getSignatureHtml } from "./printStyles";
import { downloadAsJPG, downloadAsPDF, createPrintDocument } from "./printingBridge";
import { Student } from "@/services/db/types";
import { format } from "date-fns";
import { toast } from "sonner";

// SET PROGRAMS PER PAGE TO 2 AS REQUESTED
const PROGRAMS_PER_PAGE = 2;

/**
 * Export student program details as JPG - ULTRA-FAST WITH PAGINATION
 */
export const printStudentProgramDetailsAsJPG = async (student: Student, startPage = 1, endPage?: number) => {
  try {
    const loadingToast = toast.loading('Generating student programs & fees JPG...');

    const { content, filename, styles } = generateStudentProgramContentPaginated(student, startPage, endPage);
    
    // Generate the complete print document
    const printDocument = createPrintDocument(content, filename, styles);
    
    // Download as JPG with ultra-fast settings
    await downloadAsJPG(printDocument, filename, {
      onError: (error) => {
        console.error('JPG download error:', error);
        toast.dismiss(loadingToast);
        toast.error('Failed to download student programs & fees');
      }
    });
    
    toast.dismiss(loadingToast);
    toast.success('Student programs & fees downloaded as JPG!');
    
  } catch (error) {
    console.error('Error downloading student programs & fees:', error);
    toast.error('Failed to download student programs & fees');
  }
};

/**
 * Export student program details as PDF - ULTRA-FAST WITH PAGINATION
 */
export const printStudentProgramDetailsAsPDF = async (student: Student, startPage = 1, endPage?: number) => {
  try {
    const loadingToast = toast.loading('Generating student programs & fees PDF...');

    const { content, filename, styles } = generateStudentProgramContentPaginated(student, startPage, endPage);
    
    // Generate the complete print document
    const printDocument = createPrintDocument(content, filename, styles);
    
    // Download as PDF with ultra-fast settings
    await downloadAsPDF(printDocument, filename, {
      onError: (error) => {
        console.error('PDF download error:', error);
        toast.dismiss(loadingToast);
        toast.error('Failed to download student programs & fees');
      }
    });
    
    toast.dismiss(loadingToast);
    toast.success('Student programs & fees downloaded as PDF!');
    
  } catch (error) {
    console.error('Error downloading student programs & fees:', error);
    toast.error('Failed to download student programs & fees');
  }
};

/**
 * Legacy function - now calls JPG version for backward compatibility
 */
export const printStudentProgramDetails = async (student: Student) => {
  await printStudentProgramDetailsAsJPG(student);
};

/**
 * Generate paginated content for both JPG and PDF downloads
 */
const generateStudentProgramContentPaginated = (student: Student, startPage = 1, endPage?: number) => {
  // Get full student name including surname
  const fullName = student.surname ? `${student.name} ${student.surname}` : student.name;
  
  const programs = student.programs || [];
  const totalPrograms = programs.length;
  const totalPages = Math.ceil(totalPrograms / PROGRAMS_PER_PAGE);
  
  const actualEndPage = endPage || totalPages;
  const actualStartPage = Math.max(1, Math.min(startPage, totalPages));
  const finalEndPage = Math.max(actualStartPage, Math.min(actualEndPage, totalPages));

  let allPages = '';

  for (let page = actualStartPage; page <= finalEndPage; page++) {
    const startIndex = (page - 1) * PROGRAMS_PER_PAGE;
    const endIndex = Math.min(startIndex + PROGRAMS_PER_PAGE, totalPrograms);
    const pagePrograms = programs.slice(startIndex, endIndex);

    const pageContent = `
      <div class="print-page">
        ${getHeaderHtml(`Student Programs & Fees - Page ${page} of ${totalPages}`)}
        
        <div class="print-content">
          <div class="programs-container">
            <div class="student-name">${fullName}</div>
            <div class="student-id">Student ID: ${student.id}</div>
            <div class="page-info">Page ${page} of ${totalPages} | Programs ${startIndex + 1} - ${endIndex} of ${totalPrograms}</div>
            
            ${pagePrograms.length > 0 
              ? pagePrograms.map(program => `
                  <div class="program-card">
                    <div class="program-title">${program.title}</div>
                    <div class="program-category">Category: ${program.category}</div>
                    
                    <div class="program-grid">
                      <div class="program-item">
                        <div class="program-label">Enrolled At:</div>
                        <div>${program.enrolledAt ? format(new Date(program.enrolledAt), 'PPP') : 'N/A'}</div>
                      </div>
                      <div class="program-item">
                        <div class="program-label">Completion Date:</div>
                        <div>${program.completionDate ? format(new Date(program.completionDate), 'PPP') : 'N/A'}</div>
                      </div>
                      <div class="program-item">
                        <div class="program-label">Fee Amount (AFN):</div>
                        <div>${program.fee || 0}</div>
                      </div>
                      <div class="program-item">
                        <div class="program-label">Fee Status:</div>
                        <div class="status-${program.feeStatus}">${program.feeStatus || 'pending'}</div>
                      </div>
                      <div class="program-item">
                        <div class="program-label">Duration:</div>
                        <div>${program.duration || 0} days</div>
                      </div>
                      <div class="program-item">
                        <div class="program-label">Balance (AFN):</div>
                        <div>${(program.feeStatus === 'paid' ? 0 : (program.feeStatus === 'partial' ? Math.round((program.fee || 0) / 2) : (program.fee || 0))) || 0}</div>
                      </div>
                    </div>
                  </div>
                `).join('')
              : '<p>No programs on this page</p>'
            }
          </div>
          
          ${getSignatureHtml('student')}
        </div>
        
        ${getFooterHtml('student')}
      </div>
    `;

    allPages += pageContent;
    if (page < finalEndPage) {
      allPages += '<div class="page-break"></div>';
    }
  }

  const styles = `
    ${getCommonPrintStyles()}
    .programs-container {
      margin-top: 20px;
    }
    .program-card {
      border: 1px solid #ddd;
      border-radius: 8px;
      padding: 15px;
      margin-bottom: 20px;
    }
    .program-title {
      font-size: 18px;
      font-weight: bold;
      margin-bottom: 5px;
      color: #333;
    }
    .program-category {
      font-size: 14px;
      color: #666;
      margin-bottom: 10px;
      font-weight: 500;
    }
    .program-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 10px;
    }
    .program-item {
      margin-bottom: 6px;
    }
    .program-label {
      font-weight: 500;
      color: #555;
    }
    .student-name {
      font-size: 24px;
      font-weight: bold;
      margin-bottom: 5px;
    }
    .student-id {
      font-size: 14px;
      color: #666;
      margin-bottom: 8px;
    }
    .page-info {
      font-size: 12px;
      color: #888;
      margin-bottom: 15px;
      font-style: italic;
    }
    .status-paid {
      color: #2e7d32;
      font-weight: 500;
    }
    .status-partial {
      color: #ed6c02;
      font-weight: 500;
    }
    .status-pending {
      color: #d32f2f;
      font-weight: 500;
    }
  `;

  // Create filename
  const pageRange = actualStartPage === finalEndPage ? `Page_${actualStartPage}` : `Pages_${actualStartPage}-${finalEndPage}`;
  const filename = `Student_Programs_${fullName.replace(/\s+/g, '_')}_${pageRange}_${new Date().toISOString().split('T')[0]}`;

  return { 
    content: allPages, 
    filename, 
    styles 
  };
};

/**
 * Calculate total pages for programs
 */
export const calculateProgramPages = (student: Student): number => {
  const programs = student.programs || [];
  return Math.ceil(programs.length / PROGRAMS_PER_PAGE);
};

/**
 * Get programs per page constant
 */
export const getProgramsPerPage = (): number => {
  return PROGRAMS_PER_PAGE;
};
